import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Flex,
  Heading,
  Box,
  Button,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Input,
  IconButton,
  Select
} from '@chakra-ui/react';
import SettingsBreadcrumb from '../../../components/SettingsBreadcrumb';
import TabelaPublicacaoRedeSocial from './tabela-redes-sociais';
import Paginacao from '../../../components/common/paginacao/paginacao';
import * as api from '../../../api/redesSociais';
import { AiOutlineClose, AiOutlineSearch } from 'react-icons/ai';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Dropdown, DropdownButton } from 'react-bootstrap';
import {
  faFacebookSquare, faInstagram, faLinkedin, faYoutube,
  faTiktok, faXTwitter, faWhatsapp, faTelegram, faTumblrSquare
} from '@fortawesome/free-brands-svg-icons';
import { faGlobe } from '@fortawesome/free-solid-svg-icons';

interface Situacao {
  codSituacaoPublicacao: number;
  dscSituacaoPublicacao: string;
}

const GerenciarPublicacaoRedesSociais = () => {
  const navigate = useNavigate();
  const { codRede } = useParams();
  const [idUrl, setIdUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [redes, setRedes] = useState<any[]>([]);

  const [paginaAtual, setPaginaAtual] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [tamanhoPagina, setTamanhoPagina] = useState(10);

  const [pesquisa, setPesquisa] = useState<string>('');
  const [situacao, setSituacao] = useState<number | undefined>(undefined);
  const [dateFilterType, setDateFilterType] = useState<'publicacao' | ''>('');
  const [pubInicio, setPubInicio] = useState<string>('');
  const [pubFim, setPubFim] = useState<string>('');
  const [despubInicio, setDespubInicio] = useState<string>('');
  const [despubFim, setDespubFim] = useState<string>('');
  const [estadoInputData, setEstadoInputData] = useState<'text' | 'date'>('text');

  const [situacoes, setSituacoes] = useState<Situacao[]>([]);
  const [loadingSituacoes, setLoadingSituacoes] = useState(false);
  const [errorSituacoes, setErrorSituacoes] = useState<string>();

  const formataData = (data: string) =>
    data ? data.split('-').reverse().join('/') : '';

  useEffect(() => {
    const urlAtual = window.location.hash;
    const partesUrl = urlAtual.split('/');
    const idDaUrl = partesUrl[partesUrl.length - 1];

    setIdUrl(idDaUrl);
  }, []);

  useEffect(() => {
    const fetchSituacoes = async () => {
      setLoadingSituacoes(true);
      try {
        const data = await api.obterListaFiltroSituacao();
        setSituacoes(data);
      } catch (err) {
        console.error(err);
        setErrorSituacoes('Falha ao carregar situações');
      } finally {
        setLoadingSituacoes(false);
      }
    };
    fetchSituacoes();
  }, []);

  useEffect(() => {
    setPaginaAtual(1);
  }, [pesquisa]);

  useEffect(() => {
    setPaginaAtual(1);
  }, [situacao, dateFilterType, pubInicio, pubFim, despubInicio, despubFim]);

  useEffect(() => {
    const fetchNoticias = async () => {
      setLoading(true);
      try {
        const datPubInicio = dateFilterType === 'publicacao' ? pubInicio : undefined;
        const datPubFim = dateFilterType === 'publicacao' ? pubFim : undefined;
        const codSituacaoParam = situacao !== undefined ? situacao : undefined;

        const data = await api.listarPublicacaoRedesSociais(
          Number(codRede),
          paginaAtual,
          tamanhoPagina,
          pesquisa || undefined,
          codSituacaoParam,
          datPubInicio,
          datPubFim,
        );

        const lista = Array.isArray(data.conteudo)
          ? data.conteudo.map((n: any) => ({
            seqConteudo: n.seqConteudo ?? '',
            nomePublicacao: n.nomePublicacao ?? '',
            datRegistro: n.datRegistro ?? '',
            datPublicacao: n.datPublicacao ?? '',
            datDespublicar: n.datDespublicar ?? '',
            numRegistro: n.numRegistro ?? '',
            conteudoPaginaDTO: n.conteudoPaginaDTO ?? '',
            dscSituacaoPublicacao: n.dscSituacaoPublicacao ?? '',
          }))
          : [];

        setRedes(lista);
        setTotalPages(data.totalPaginas ?? 1);
      } catch (err) {
        console.error('Erro ao buscar notícias:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchNoticias();
  }, [
    paginaAtual,
    tamanhoPagina,
    pesquisa,
    situacao,
    dateFilterType,
    pubInicio, pubFim,
    despubInicio, despubFim,
  ]);

  const iconeRedeSocial = (id: number) => {
    const icones: { [key: number]: { icon: any, color: string } } = {
      0: { icon: faFacebookSquare, color: "#1789fc" },
      1: { icon: faInstagram, color: "#ec1393" },
      2: { icon: faLinkedin, color: "#1DA1F2" },
      3: { icon: faYoutube, color: "#ff0000" },
      4: { icon: faTiktok, color: "#080404" },
      5: { icon: faXTwitter, color: "#080404" },
      6: { icon: faWhatsapp, color: "#43e460" },
      7: { icon: faTelegram, color: "#088ccc" },
      8: { icon: faTumblrSquare, color: "#384c5c" },
    };

    return icones[id] || { icon: faGlobe, color: "#3b87d8" };
  };

  const tituloRede = (codigo: number) => {
    const titles: { [key: number]: string } = {
      0: 'Facebook',
      1: 'Instagram',
      2: 'LinkedIn',
      3: 'Youtube',
      4: 'TikTok',
      5: 'X (Antigo Twitter)',
      6: 'Whatsapp',
      7: 'Telegram',
      8: 'Tumblr',
      9: 'Página Internet',
    };

    return titles[codigo] || '';
  };

  const handleDeleteRedeSocial = (seqRedeDeletada: number) => {
    setRedes((prevRedes) =>
      prevRedes.filter((rede) => rede.seqConteudo !== seqRedeDeletada)
    );
  };

  return (
    <Flex p="40px" bgColor="#fafafa" direction="column" w="100%">
      <SettingsBreadcrumb />
      <Heading
        mt="30px"
        fontWeight="600"
        fontSize="21px"
        color="#6F6F6F"
        w="100%"
      >
        Gerenciar publicações
      </Heading>
      <Heading display={'flex'} alignItems={'center'} mt="20px" fontWeight="500" fontSize="20px" color="#3b3b3b" w="100%">
        <FontAwesomeIcon
          style={{ marginRight: '10px', fontSize: '25px' }}
          icon={iconeRedeSocial(Number(idUrl)).icon}
          color={iconeRedeSocial(Number(idUrl)).color}
        />
        {`${tituloRede(Number(idUrl))}`}
      </Heading>

      <Flex
        w="100%"
        mt="40px"
        mb="20px"
        align="flex-start"
        justify="space-between"
        flexWrap="wrap"
        gap="16px"
      >
        <Button
          color="#fff"
          bgColor="#1789FC"
          _hover={{
            bgColor: "#1468C9"
          }}
          onClick={() => navigate(`incluir-rede-social`)}
        >
          Nova Publicação
        </Button>

        <Box flex="1" minW="280px">
          <InputGroup mb="12px">
            <InputLeftElement pointerEvents="none">
              <AiOutlineSearch color="#6e6e6e" />
            </InputLeftElement>
            <Input
              bg="white"
              placeholder="Pesquisar por título..."
              value={pesquisa}
              onChange={e => setPesquisa(e.target.value)}
            />
            {pesquisa && (
              <InputRightElement>
                <IconButton
                  size="sm"
                  variant="ghost"
                  aria-label="Limpar busca"
                  icon={<AiOutlineClose />}
                  onClick={() => setPesquisa('')}
                />
              </InputRightElement>
            )}
          </InputGroup>

          <Flex wrap="wrap" gap="12px" justifyContent="flex-end">
            <DropdownButton
            id="dropdown-situacao"
            size="sm"
            title={
              situacao
                ? situacoes.find(s => s.codSituacaoPublicacao === situacao)?.dscSituacaoPublicacao
                : "Situação"
            }
            className="custom-dropdown"
          >
            {situacoes.map(s => (
              <Dropdown.Item
                key={s.codSituacaoPublicacao}
                onClick={() => setSituacao(s.codSituacaoPublicacao)}
              >
                {s.dscSituacaoPublicacao}
              </Dropdown.Item>
            ))}
            <Dropdown.Divider />
            <Dropdown.Item onClick={() => setSituacao(undefined)}>
              Limpar filtro
            </Dropdown.Item>
          </DropdownButton>

          <DropdownButton
            id="dropdown-periodo"
            size="sm"
            title={
              dateFilterType === "publicacao"
                ? "Data de publicação"
                : "Período"
            }
            className="custom-dropdown"
          >
            <Dropdown.Item onClick={() => setDateFilterType("publicacao")}>
              Data de publicação
            </Dropdown.Item>

            <Dropdown.Divider />

            <Dropdown.Item onClick={() => setDateFilterType("")}>
              Limpar período
            </Dropdown.Item>
          </DropdownButton>

            {dateFilterType && (
              <>
                <Input
                  type={estadoInputData}
                  size="sm"
                  w="160px"
                  value={
                    estadoInputData === 'date'
                      ? (dateFilterType === 'publicacao' ? pubInicio : despubInicio)
                      : (dateFilterType === 'publicacao'
                        ? formataData(pubInicio)
                        : formataData(despubInicio))
                  }
                  onChange={e => {
                    if (estadoInputData === 'date') {
                      return dateFilterType === 'publicacao'
                        ? setPubInicio(e.target.value)
                        : setDespubInicio(e.target.value);
                    }
                  }}
                  onFocus={() => setEstadoInputData('date')}
                  onBlur={() => setEstadoInputData('text')}
                  placeholder="De"
                  color="#6e6e6e"
                  readOnly={estadoInputData === 'text'}
                  _placeholder={{ color: '#6e6e6e' }}
                />

                <Input
                  type={estadoInputData}
                  size="sm"
                  w="160px"
                  value={
                    estadoInputData === 'date'
                      ? (dateFilterType === 'publicacao' ? pubFim : despubFim)
                      : (dateFilterType === 'publicacao'
                        ? formataData(pubFim)
                        : formataData(despubFim))
                  }
                  onChange={e => {
                    if (estadoInputData === 'date') {
                      return dateFilterType === 'publicacao'
                        ? setPubFim(e.target.value)
                        : setDespubFim(e.target.value);
                    }
                  }}
                  onFocus={() => setEstadoInputData('date')}
                  onBlur={() => setEstadoInputData('text')}
                  placeholder="Até"
                  color="#6e6e6e"
                  readOnly={estadoInputData === 'text'}
                  _placeholder={{ color: '#6e6e6e' }}
                />
              </>
            )}

            {(situacao !== undefined ||
              dateFilterType ||
              pubInicio ||
              pubFim ||
              despubInicio ||
              despubFim) && (
                <IconButton
                  size="sm"
                  variant="outline"
                  aria-label="Limpar filtros"
                  icon={<AiOutlineClose />}
                  onClick={() => {
                    setSituacao(undefined);
                    setDateFilterType('');
                    setPubInicio('');
                    setPubFim('');
                    setDespubInicio('');
                    setDespubFim('');
                  }}
                />
              )}
          </Flex>
        </Box>

      </Flex>

      <Box position="relative" mt={5}>
        <TabelaPublicacaoRedeSocial
          listaRedes={redes}
          listaVazia="Nenhum dado disponível."
          loading={loading}
          onDelete={handleDeleteRedeSocial}
        />
      </Box>

      <Paginacao
        paginaAtual={paginaAtual}
        totalPages={totalPages}
        onPageChange={setPaginaAtual}
      />
    </Flex>
  );
};

export default GerenciarPublicacaoRedesSociais;
